/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/

/**
 * Cookie service for managing session tokens and assignment-related cookies
 */

const COOKIE_NAMES = {
  SESSION_TOKEN: 'capture_session_token',
};

/**
 * Set a cookie with given name, value, and options
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options (expires, path, secure, sameSite)
 */
export const setCookie = (name, value, options = {}) => {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options.expires) {
    cookieString += `; expires=${options.expires.toUTCString()}`;
  }

  cookieString += `; path=${options.path || '/'}`;

  if (options.secure) {
    cookieString += '; secure';
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const deleteCookie = (name, path = '/') => {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
};

/**
 * Store session token for a capture assignment
 * @param {string} token - Assignment token
 * @param {string} sessionToken - Session token (uid) returned from server
 */
export const setSessionToken = (token, sessionToken) => {
  const cookieName = `${COOKIE_NAMES.SESSION_TOKEN}_${token}`;
  
  // Set cookie to expire in 30 minutes (matching backend SESSION_EXPIRY_MINUTES)
  const expires = new Date();
  expires.setMinutes(expires.getMinutes() + 30);

  setCookie(cookieName, sessionToken, {
    expires,
    path: '/',
    sameSite: 'Lax',
  });
};

/**
 * Get session token for a capture assignment
 * @param {string} token - Assignment token
 * @returns {string|null} Session token or null if not found
 */
export const getSessionToken = (token) => {
  const cookieName = `${COOKIE_NAMES.SESSION_TOKEN}_${token}`;
  return getCookie(cookieName);
};

/**
 * Delete session token for a capture assignment
 * @param {string} token - Assignment token
 */
export const deleteSessionToken = (token) => {
  const cookieName = `${COOKIE_NAMES.SESSION_TOKEN}_${token}`;
  deleteCookie(cookieName);
};

/**
 * Check if a session token exists and is valid for a given assignment
 * @param {string} token - Assignment token
 * @returns {boolean} True if session token exists
 */
export const hasValidSessionToken = (token) => {
  return getSessionToken(token) !== null;
};
